<script setup>
import { supabase } from "../lib/supabase.js";
import { useRouter } from "vue-router";
import { useUserStore } from "../stores/user.js";
import { ref, onMounted, computed } from "vue";
import {
  createUserFlashcards,
  updateUserAbilityDistribution,
  GAUSSIAN_DEFAULTS_BY_LEVEL,
  setOnboardingStartingRank,
} from "../Scripts/dbFunctions.js";

const router = useRouter();
const userStore = useUserStore();

const prefs = ref(null);
const activeLanguage = ref("");
const darkMode = ref(false);
const loadError = ref(null);
const loadingPrefs = ref(false);

// Proficiency modal state
const showProficiencyModal = ref(false);
const selectedProficiencyLevel = ref("");
const savingProficiency = ref(false);
const proficiencyOptions = [
  { value: "pre-beginner", label: "Pre-beginner" },
  { value: "beginner", label: "Beginner" },
  { value: "intermediate", label: "Intermediate" },
  { value: "advanced", label: "Advanced" },
];

const isSubscribed = computed(() => {
  const status = prefs.value?.subscription_status?.toLowerCase?.() || "";
  if (!status) return false;
  if (status === "lifetime") return true;
  if (status === "active" || status === "trialing") return true;
  if (status === "canceled") {
    const endIso = prefs.value?.current_period_end || null;
    const endMs = endIso ? Date.parse(endIso) : null;
    return !!endMs && endMs > Date.now();
  }
  return false;
});

// Map Stripe price_id to human-readable plan names.
// Add additional mappings here as needed.
const PLAN_NAMES = {
  price_1S1aDNQHyLSDk8AHYQeCeBkN: "Monthly Subscription",
  price_1S1aEmQHyLSDk8AHs4G0NKxC: "Yearly Subscription",
  price_1SB3fiQHyLSDk8AHw8CntC0Q: "Lifetime Access",
};

const PLAN_PRICES = {
  price_1S1aDNQHyLSDk8AHYQeCeBkN: "$2.99/mo",
  price_1S1aEmQHyLSDk8AHs4G0NKxC: "$28.99/yr",
  price_1SB3fiQHyLSDk8AHw8CntC0Q: "$49.99 one-time",
};

const planType = computed(() => {
  const status = prefs.value?.subscription_status;
  if (typeof status === "string" && status.toLowerCase() === "free trial") {
    return "Free Trial";
  }
  const id = prefs.value?.price_id;
  return id ? PLAN_NAMES[id] ?? id : "—";
});

const planPrice = computed(() => {
  const status = prefs.value?.subscription_status;
  if (typeof status === "string" && status.toLowerCase() === "free trial") {
    return "$0";
  }
  const id = prefs.value?.price_id;
  return id ? PLAN_PRICES[id] ?? "—" : "—";
});

const showManageButton = computed(() => {
  const status = prefs.value?.subscription_status?.toLowerCase?.() || "";
  // Show Manage when: active/trialing, or canceled but still within paid period; never for lifetime or free trial
  if (status === "lifetime" || status === "free trial") return false;
  if (status === "active" || status === "trialing") return true;
  if (status === "canceled") {
    const endIso = prefs.value?.current_period_end || null;
    const endMs = endIso ? Date.parse(endIso) : null;
    return !!endMs && endMs > Date.now();
  }
  return false;
});
const userEmail = computed(() => userStore?.user?.email ?? "—");
const userName = computed(() => userStore?.user?.user_metadata?.name ?? "—");
const totalAnswered = ref(0);
const cardsLeft = computed(() =>
  Math.max(0, 150 - Number(totalAnswered.value || 0))
);
const isOnUsageFreeTrial = computed(
  () => !isSubscribed.value && cardsLeft.value > 0
);

onMounted(async () => {
  try {
    const uid = userStore?.user?.id;
    if (!uid) return;
    console.log("AccountSettings: requesting user_preferences", { uid });

    // Log session/JWT presence
    const { data: sessData } = await supabase.auth.getSession();
    console.log("AccountSettings: session", {
      hasJWT: !!sessData?.session?.access_token,
    });

    loadingPrefs.value = true;

    // First attempt: include auto_renew
    let { data, error } = await supabase
      .from("user_preferences")
      .select(
        "price_id, current_period_end, subscription_status, auto_renew, dark_mode"
      )
      .eq("user_id", uid)
      .maybeSingle();

    // If we hit a 406 or header/profile issue, fall back without auto_renew
    if (error) {
      const is406 =
        error?.code === "406" ||
        error?.code === "PGRST116" ||
        String(error?.message || "").includes("406") ||
        String(error?.details || "").includes("406");

      loadError.value = {
        stage: "first",
        code: error?.code,
        message: error?.message,
        details: error?.details,
        is406,
      };

      console.warn(
        "AccountSettings: failed to load user_preferences (first try)",
        {
          uid,
          code: error?.code,
          message: error?.message,
          details: error?.details,
          is406,
        }
      );

      if (is406) {
        console.warn(
          "AccountSettings: retrying user_preferences without auto_renew column"
        );
        const fb = await supabase
          .from("user_preferences")
          .select("price_id, current_period_end, subscription_status")
          .eq("user_id", uid)
          .maybeSingle();

        if (fb.error) {
          console.warn("AccountSettings: fallback query failed", {
            uid,
            code: fb.error?.code,
            message: fb.error?.message,
            details: fb.error?.details,
          });
          loadError.value = {
            stage: "fallback_no_auto_renew",
            code: fb.error?.code,
            message: fb.error?.message,
            details: fb.error?.details,
          };
        } else if (fb.data) {
          console.log(
            "AccountSettings: loaded user_preferences (fallback)",
            fb.data
          );
          prefs.value = fb.data;
          loadError.value = null;
        }

        // If still no prefs, try full select for diagnostics and to pick known fields
        if (!prefs.value) {
          console.warn(
            "AccountSettings: retrying user_preferences with select('*') for diagnostics"
          );
          const fb2 = await supabase
            .from("user_preferences")
            .select("*")
            .eq("user_id", uid)
            .maybeSingle();

          if (fb2.error) {
            console.warn("AccountSettings: full select fallback failed", {
              uid,
              code: fb2.error?.code,
              message: fb2.error?.message,
              details: fb2.error?.details,
            });
            loadError.value = {
              stage: "fallback_full",
              code: fb2.error?.code,
              message: fb2.error?.message,
              details: fb2.error?.details,
            };
          } else if (fb2.data) {
            console.log(
              "AccountSettings: loaded user_preferences (full select)",
              fb2.data
            );
            const d = fb2.data;
            prefs.value = {
              price_id: d?.price_id ?? null,
              current_period_end: d?.current_period_end ?? null,
              subscription_status: d?.subscription_status ?? null,
              auto_renew: d?.auto_renew ?? null,
              dark_mode: d?.dark_mode ?? null,
            };
            darkMode.value = !!d?.dark_mode;
            document.documentElement.setAttribute(
              "data-theme",
              darkMode.value ? "dark" : "light"
            );
            document.body.style.backgroundColor = darkMode.value
              ? "#0f172a"
              : "#ffffff";
            (function () {
              const themeColor = darkMode.value ? "#0f172a" : "#ffffff";
              let meta =
                document.querySelector(
                  'meta#dynamic-theme-color[name="theme-color"]'
                ) || document.querySelector('meta[name="theme-color"]');
              if (meta) meta.setAttribute("content", themeColor);
            })();
            loadError.value = null;
          }
        }
      }
    }

    // If first attempt succeeded, set prefs
    if (data) {
      console.log("AccountSettings: loaded user_preferences", data);
      prefs.value = data;
      darkMode.value = !!data.dark_mode;
      document.documentElement.setAttribute(
        "data-theme",
        darkMode.value ? "dark" : "light"
      );
      document.body.style.backgroundColor = darkMode.value
        ? "#0f172a"
        : "#ffffff";
      (function () {
        const themeColor = darkMode.value ? "#0f172a" : "#ffffff";
        let meta =
          document.querySelector(
            'meta#dynamic-theme-color[name="theme-color"]'
          ) || document.querySelector('meta[name="theme-color"]');
        if (meta) meta.setAttribute("content", themeColor);
      })();
      loadError.value = null;
    }

    if (!data && !prefs.value) {
      console.log("AccountSettings: no user_preferences row for user", { uid });
    }
  } catch (e) {
    console.error("Failed to fetch subscription prefs:", e);
    loadError.value = { stage: "exception", message: e?.message || String(e) };
  } finally {
    loadingPrefs.value = false;
  }
});

const handleLogout = async () => {
  try {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;

    // Clear user store and navigate to login
    userStore.user = null;
    userStore.isLoggedIn = false;

    router.replace("/login");
  } catch (err) {
    console.error("Logout error:", err);
  }
};
const handleSubscribe = async () => {
  try {
    router.push("/subscriptions");
  } catch (err) {
    console.error("Subscribe nav error:", err);
  }
};
const handleManageSubscription = async () => {
  try {
    const { data, error } = await supabase.functions.invoke(
      "create-portal-session"
    );
    if (error) throw error;
    if (data?.url) {
      window.location.href = data.url;
    }
  } catch (err) {
    console.error("Manage subscription error:", err);
  }
};

// Proficiency modal controls
const openProficiencyModal = () => {
  selectedProficiencyLevel.value = "";
  showProficiencyModal.value = true;
};
const closeProficiencyModal = () => {
  showProficiencyModal.value = false;
};
const saveProficiency = async () => {
  if (!selectedProficiencyLevel.value) return;
  try {
    savingProficiency.value = true;
    const key = selectedProficiencyLevel.value;
    const code = activeLanguage.value;
    const defaults = GAUSSIAN_DEFAULTS_BY_LEVEL[key];

    // Apply Rasch prior when available (beginner/intermediate/advanced)
    if (defaults && code) {
      const res = await updateUserAbilityDistribution(
        defaults.mean,
        defaults.var,
        code
      );
      if (!res?.success) {
        console.error(
          "Failed to set language_ability_distribution for language:",
          res?.error || res
        );
      }
    }

    // For pre-beginner: set starting rank to 0 and initialize deck scheduling
    if (key === "pre-beginner" && code) {
      const r = await setOnboardingStartingRank(code, 0);
      if (!r?.success) {
        console.error("Failed to set starting_word_rank to 0:", r?.error || r);
      } else {
        try {
          const { data: auth } = await supabase.auth.getUser();
          const uid = auth?.user?.id || null;
          if (!uid) {
            console.warn(
              "[AccountSettings] skipping FSRS schedule: missing user id"
            );
          } else {
            const payload = {
              user_id: uid,
              language_code: code,
              starting_word_rank: 0,
              fsrs_defaults: { difficulty: 1, stability: 25, state: 3 },
            };
            supabase.functions
              .invoke("apply-fsrs-schedule", { body: payload })
              .then((res) => {
                console.log(
                  "[AccountSettings] Edge FSRS schedule result",
                  res?.data ?? res
                );
              })
              .catch((err) => {
                console.error(
                  "[AccountSettings] Edge FSRS schedule error",
                  err
                );
              });
          }
        } catch (e) {
          console.error("[AccountSettings] Edge FSRS schedule exception", e);
        }
      }
    }
  } catch (e) {
    console.error("saveProficiency error:", e);
  } finally {
    savingProficiency.value = false;
    closeProficiencyModal();
  }
};

const toggleDarkMode = async () => {
  try {
    const uid = userStore?.user?.id;
    if (!uid) return;
    const value = !!darkMode.value;
    // Apply theme immediately
    document.documentElement.setAttribute(
      "data-theme",
      value ? "dark" : "light"
    );
    // Also update body background and theme-color meta so UI updates without refresh
    document.body.style.backgroundColor = value ? "#0f172a" : "#ffffff";
    (function () {
      const themeColor = value ? "#0f172a" : "#ffffff";
      let meta =
        document.querySelector(
          'meta#dynamic-theme-color[name="theme-color"]'
        ) || document.querySelector('meta[name="theme-color"]');
      if (meta) meta.setAttribute("content", themeColor);
    })();

    const { error } = await supabase
      .from("user_preferences")
      .upsert({ user_id: uid, dark_mode: value });
    if (error) {
      console.error("Failed to update dark_mode:", error);
    }
  } catch (e) {
    console.error("toggleDarkMode error:", e);
  }
};
const updateActiveLanguage = async () => {
  try {
    const uid = userStore?.user?.id;
    if (!uid) return;
    const code = activeLanguage.value;
    if (!code) return;

    // Show proficiency prompt immediately to avoid delay
    openProficiencyModal();

    // Update the active language in the background
    (async () => {
      try {
        const { error } = await supabase
          .from("user_preferences")
          .upsert({ user_id: uid, active_language: code });
        if (error) {
          console.error("Failed to update active_language:", error);
        }
      } catch (err) {
        console.error("Background active_language update error:", err);
      }
    })();

    // Initialize user_cards for the new language in the background
    (async () => {
      try {
        await createUserFlashcards(code, uid);
      } catch (e) {
        console.error("Failed to initialize user_cards for language:", code, e);
      }
    })();

    // Check if language_ability_distribution already exists; if so, close modal
    (async () => {
      try {
        const { data: pref, error: selErr } = await supabase
          .from("user_preferences")
          .select("language_ability_distribution")
          .eq("user_id", uid)
          .maybeSingle();

        if (selErr) {
          console.error("Error reading language_ability_distribution:", selErr);
          return;
        }

        const dist = pref?.language_ability_distribution || {};
        const hasDist = dist && typeof dist === "object" && dist[code];

        if (hasDist) {
          closeProficiencyModal();
        }
      } catch (e) {
        console.error(
          "Failed to check/update language_ability_distribution:",
          e
        );
      }
    })();
  } catch (e) {
    console.error("updateActiveLanguage error:", e);
  }
};

onMounted(async () => {
  try {
    const uid = userStore?.user?.id;
    if (!uid) return;
    const { data, error } = await supabase
      .from("user_preferences")
      .select("active_language")
      .eq("user_id", uid)
      .maybeSingle();
    if (!error && data) {
      activeLanguage.value = data.active_language || "";
    }
  } catch (e) {
    console.error("Failed to load active_language:", e);
  }
});
onMounted(async () => {
  if (!userStore?.user) {
    await userStore.refreshFromSession();
  }
  try {
    const uid = userStore?.user?.id;
    if (!uid) return;
    const { count, error } = await supabase
      .from("card_actions")
      .select("*", { count: "exact", head: true })
      .eq("user_id", uid);
    totalAnswered.value = !error && typeof count === "number" ? count : 0;
  } catch (_e) {
    totalAnswered.value = 0;
  }
});
</script>

<template>
  <div class="main-content">
    <div class="account-settings-container">
      <button @click="handleLogout" class="logout-button">Logout</button>

      <h1>Account Settings</h1>

      <div class="account-info-card">
        <h2>Account Information</h2>
        <div class="row">
          <span class="label">Name</span>
          <span class="value">{{ userName }}</span>
        </div>
        <div class="row">
          <span class="label">Email</span>
          <span class="value">{{ userEmail }}</span>
        </div>
      </div>

      <div class="preferences-card">
        <h2 margin-top="5px">App Preferences</h2>
        <div class="row">
          <span class="label">Active language</span>
          <select v-model="activeLanguage" @change="updateActiveLanguage">
            <option value="es">Spanish</option>
            <option value="fr">French</option>
            <option value="it">Italian</option>
            <option value="zh">Mandarin</option>
          </select>
        </div>
        <div class="row">
          <span class="label">Dark mode</span>
          <label class="switch">
            <input
              type="checkbox"
              v-model="darkMode"
              @change="toggleDarkMode"
            />
            <span class="slider"></span>
          </label>
        </div>
      </div>

      <div class="subscription-card">
        <h2>Subscription Information</h2>
        <div v-if="loadError" class="error">
          Unable to load subscription details. Please try again.
        </div>
        <div class="row">
          <span class="label">Plan type</span>
          <span class="value">{{
            isOnUsageFreeTrial ? "Free Trial" : planType
          }}</span>
        </div>
        <div class="row">
          <span class="label">Current period end</span>
          <span class="value">{{
            isOnUsageFreeTrial
              ? cardsLeft + " cards left"
              : prefs?.current_period_end
              ? new Date(prefs.current_period_end).toISOString().slice(0, 10)
              : "—"
          }}</span>
        </div>
        <div class="row">
          <span class="label">Auto-renewal</span>
          <span class="value">
            {{
              isOnUsageFreeTrial
                ? "Will not renew"
                : typeof prefs?.subscription_status === "string" &&
                  prefs.subscription_status.toLowerCase() === "free trial"
                ? "Will not renew"
                : prefs?.auto_renew === true
                ? "Will renew"
                : prefs?.auto_renew === false
                ? "Will not renew"
                : "—"
            }}
          </span>
        </div>
        <div class="row">
          <span class="label">Price</span>
          <span class="value">{{ isOnUsageFreeTrial ? "$0" : planPrice }}</span>
        </div>
        <button
          v-if="showManageButton"
          class="subscribe-button"
          @click="handleManageSubscription()"
        >
          Manage subscription
        </button>
        <button
          v-else-if="!isSubscribed"
          class="subscribe-button"
          @click="handleSubscribe()"
        >
          Subscribe
        </button>
      </div>

      <div class="support-card">
        <h2>Support</h2>
        <div class="row">
          <span class="label">Support Contact</span>
          <span class="value">Taylor Rubalcava</span>
        </div>
        <div class="row">
          <span class="label">Email</span>
          <span class="value">
            <a
              href="mailto:taylorboan@me.com?subject=Lexical Support Request"
              class="support-email"
            >
              taylorboan@me.com
            </a>
          </span>
        </div>
      </div>
    </div>
  </div>

  <div
    v-if="showProficiencyModal"
    class="proficiency-modal-overlay"
    @click.self="closeProficiencyModal"
  >
    <div
      class="proficiency-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="proficiency-title"
    >
      <h3 id="proficiency-title">Set your proficiency</h3>
      <p class="helper">
        Choose your proficiency level for the selected language.
      </p>
      <select
        v-model="selectedProficiencyLevel"
        class="proficiency-select"
        :disabled="savingProficiency"
      >
        <option disabled value="">Choose your proficiency...</option>
        <option
          v-for="opt in proficiencyOptions"
          :key="opt.value"
          :value="opt.value"
        >
          {{ opt.label }}
        </option>
      </select>
      <div class="proficiency-actions">
        <button
          type="button"
          class="cancel-btn"
          @click="closeProficiencyModal"
          :disabled="savingProficiency"
        >
          Cancel
        </button>
        <button
          type="button"
          class="submit-btn"
          @click="saveProficiency"
          :disabled="!selectedProficiencyLevel || savingProficiency"
        >
          {{ savingProficiency ? "Saving..." : "Save" }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.main-content {
  margin-left: 75px; /* same as .nav-bar width */
  padding: 20px; /* optional spacing */
}

.account-settings-container {
  position: relative;
  padding: 2rem;
  min-height: 100vh;
}

/* Subscription details card */
.subscription-card {
  padding: 16px;
  border-radius: 8px;
  background: var(--sidebar-bg);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
  max-width: 520px;
  margin-bottom: 16px;
}

/* Support card */
.support-card {
  padding: 16px;
  border-radius: 8px;
  background: var(--sidebar-bg);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
  max-width: 520px;
}
.support-card h2 {
  margin: 0 0 12px;
  font-weight: 500;
  color: var(--text-color);
}
.support-card .row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin: 6px 0;
  color: var(--text-color);
}
.support-card .label {
  color: var(--text-color);
  margin-right: 15px;
}
.support-card .value {
  font-weight: 600;
}
.support-email {
  color: #2563eb;
  text-decoration: none;
  transition: color 0.2s ease;
}
.support-email:hover {
  color: #1e3a8a;
  text-decoration: underline;
}

/* App Preferences card */
.preferences-card {
  padding: 16px;
  border-radius: 8px;
  background: var(--sidebar-bg);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
  max-width: 520px;
  margin-bottom: 16px;
}
.preferences-card h2 {
  margin-top: 0px;
  font-weight: 500;
}
.preferences-card .row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 6px 0;
}
.preferences-card .row + .row {
  margin-top: 18px;
}

/* Clean, borderless pill select for Active language */
.preferences-card select {
  -webkit-appearance: none;
  appearance: none;
  background: var(--hover-bg);
  color: var(--text-color);
  border: none;
  border-radius: 9999px;
  padding: 8px 36px 8px 12px;
  font: inherit;
  line-height: 1.2;
  outline: none;
  box-shadow: none;
  background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="10" height="6" viewBox="0 0 10 6" fill="none"><path d="M1 1l4 4 4-4" stroke="%23a1a1aa" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>');
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 10px 6px;
  min-width: 160px;
}

[data-theme="dark"] .preferences-card select {
  background: var(--hover-bg);
  color: var(--text-color);
  background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="10" height="6" viewBox="0 0 10 6" fill="none"><path d="M1 1l4 4 4-4" stroke="%23d1d5db" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>');
}

.preferences-card select:focus {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
}

@media (max-width: 768px) {
  .preferences-card select {
    width: 100%;
  }
}

.account-info-card {
  padding: 16px;
  border-radius: 8px;
  background: var(--sidebar-bg);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
  max-width: 520px;
  margin-bottom: 16px;
}
.account-info-card h2 {
  margin: 0 0 12px;
  font-weight: 500;
  color: var(--text-color);
}
.account-info-card .row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin: 6px 0;
  color: var(--text-color);
}
.account-info-card .label {
  color: var(--text-color);
}
.account-info-card .value {
  font-weight: 600;
}

.subscription-card h2 {
  margin: 0 0 12px;
  font-weight: 500;
  color: var(--text-color);
}
.subscription-card .row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin: 6px 0;
  color: var(--text-color);
}
.subscription-card .label {
  color: var(--text-color);
  margin-right: 15px;
}
.subscription-card .value {
  font-weight: 600;
}

/* Toggle switch */
.switch {
  position: relative;
  display: inline-block;
  width: 48px;
  height: 26px;
  margin-left: 8px;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  inset: 0;
  background-color: #cbd5e1; /* slate-300 */
  transition: 0.2s;
  border-radius: 9999px;
}

.slider:before {
  position: absolute;
  content: "";
  height: 20px;
  width: 20px;
  left: 3px;
  top: 3px;
  background-color: #ffffff;
  transition: 0.2s;
  border-radius: 9999px;
}

.switch input:checked + .slider {
  background-color: #10b981; /* emerald-500 */
}

.switch input:checked + .slider:before {
  transform: translateX(22px);
}

.subscription-card .error {
  margin: 8px 0 12px;
  color: #b91c1c;
  font-size: 0.9rem;
}

/* Primary action: See Subscription Details */
.subs-button {
  padding: 0.8rem 1.5rem;
  background-color: #2563eb; /* Blue */
  color: #ffffff;
  border: none;
  border-radius: 25px;
  cursor: pointer;
  font-size: 1rem;
  transition: all 0.3s ease;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.08);
}
.subs-button:hover {
  background-color: #1e3a8a; /* Darker blue */
  transform: translateY(-1px);
}

/* Subscribe button */
.subscribe-button {
  margin-top: 1rem;
  padding: 0.8rem 1.5rem;
  background-color: #10b981; /* Emerald */
  color: #ffffff;
  border: none;
  border-radius: 25px;
  cursor: pointer;
  font-size: 1rem;
  transition: all 0.3s ease;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.08);
}
.subscribe-button:hover {
  background-color: #047857; /* Darker emerald */
  transform: translateY(-1px);
}

.logout-button {
  position: fixed;
  right: 20px;
  /* Support safe-area inset on iOS */
  bottom: 20px;
  bottom: calc(20px + env(safe-area-inset-bottom));
  bottom: calc(20px + constant(safe-area-inset-bottom));
  padding: 0.8rem 1.5rem;
  background-color: #e46962; /* Pastel red */
  color: #ffffff;
  border: none;
  border-radius: 25px;
  cursor: pointer;
  font-size: 1rem;
  transition: all 0.3s ease;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  z-index: 110; /* above content but below modal overlays */
}

.logout-button:hover {
  background-color: #4e3534; /* Slightly darker pastel red */
  transform: translateY(-1px);
}

h1 {
  color: var(--text-color);
  margin-bottom: 2rem;
}

.label {
  margin-right: 15px;
}

@media (max-width: 768px) {
  .main-content {
    margin-left: 0;
    padding: 16px;
  }

  .account-settings-container {
    padding: 1rem;
    min-height: auto;
    padding-bottom: 80px;
  }

  .subscription-card {
    padding: 12px;
    max-width: 100%;
  }

  .support-card {
    padding: 12px;
    max-width: 100%;
  }

  .preferences-card {
    padding: 12px;
    max-width: 100%;
  }
  .preferences-card .row {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }

  .account-info-card {
    padding: 12px;
    max-width: 100%;
  }

  .subscription-card .row {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  .support-card .row {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  .account-info-card .row {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  .subs-button,
  .subscribe-button {
    width: 100%;
  }

  .logout-button {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    margin: 0;
    border-radius: 0;
    z-index: 5;
  }

  .label {
    margin-right: 15px;
  }
}

/* Add more styles for account settings content as needed */

/* Proficiency modal (desktop + mobile), similar to report modal */
.proficiency-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
.proficiency-modal {
  width: 100%;
  max-width: 520px;
  background: var(--sidebar-bg);
  color: var(--text-color);
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
  padding: 16px;
  box-sizing: border-box;
  max-height: 90vh;
  overflow: auto;
  margin: 12px;
}
.proficiency-modal h3 {
  margin: 0 0 6px 0;
}
.proficiency-modal p.helper {
  margin: 0 0 12px 0;
  color: #8a8a8a;
}
.proficiency-select {
  width: 100%;
  max-width: 320px;
  margin: 0 auto;
  display: block;
  border: 1px solid var(--hover-bg);
  border-radius: 9999px;
  padding: 8px 12px;
  background: var(--hover-bg);
  color: var(--text-color);
  font: inherit;
  box-sizing: border-box;
}

@media (max-width: 768px) {
  .proficiency-select {
    max-width: 100%;
  }
}
.proficiency-actions {
  margin-top: 12px;
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}
.proficiency-actions .cancel-btn {
  background: var(--hover-bg);
  color: var(--text-color);
  border: none;
  padding: 8px 12px;
  border-radius: 8px;
  cursor: pointer;
}
.proficiency-actions .submit-btn {
  background: var(--accent);
  color: #fff;
  border: none;
  padding: 8px 12px;
  border-radius: 8px;
  cursor: pointer;
}
.proficiency-actions .submit-btn[disabled],
.proficiency-actions .cancel-btn[disabled] {
  opacity: 0.7;
  cursor: default;
}
</style>
